// frontend/src/services/api.js

// Base URLs e fallback
function sanitizeBaseUrl(value) {
  let raw = String(value || '').trim().replace(/\/+$/, '');
  if (!raw) return '';
  const first = raw[0];
  const last = raw[raw.length - 1];
  if ((first === '"' && last === '"') || (first === "'" && last === "'") || (first === '`' && last === '`')) {
    raw = raw.slice(1, -1).trim().replace(/\/+$/, '');
    if (!raw) return '';
  }
  try {
    const parsed = new URL(raw);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.origin;
  } catch (_) {}
  return '';
}

function deriveBackendFromLocation() {
  try {
    const origin = String(window.location.origin || '').trim().replace(/\/+$/, '');
    if (!origin) return '';
    const url = new URL(origin);
    if (url.port === '3000') url.port = '5000';
    if (url.port === '3001') url.port = '5001';
    return url.origin;
  } catch (_) {
    return '';
  }
}

const PRIMARY_API_URL = (() => {
  const envUrl = sanitizeBaseUrl(process.env.REACT_APP_API_URL);
  if (envUrl) {
    try {
      const origin = String(window.location.origin || '').trim().replace(/\/+$/, '');
      if (origin) {
        const current = new URL(origin);
        const env = new URL(envUrl);
        const isLocalHost = current.hostname === 'localhost' || current.hostname === '127.0.0.1';
        const isEnvLoopback = env.hostname === 'localhost' || env.hostname === '127.0.0.1';
        const isDevFrontendPort = current.port === '3000' || current.port === '3001';
        if (!isLocalHost && isEnvLoopback) {
          if (isDevFrontendPort) {
            const derived = new URL(current.toString());
            derived.port = current.port === '3001' ? '5001' : '5000';
            return derived.origin;
          }
          return current.origin;
        }
        if (!isLocalHost && !isDevFrontendPort && env.hostname === current.hostname && env.port === '5000' && current.port && current.port !== '5000') {
          return current.origin;
        }
      }
    } catch (_) {}
    return envUrl;
  }
  return deriveBackendFromLocation();
})();

const DERIVED_BACKEND_URL = (() => {
  try {
    const derived = deriveBackendFromLocation();
    if (derived) return derived;
    const origin = (window.location.origin || '').replace(/\/+$/, '');
    return sanitizeBaseUrl(origin) || '';
  } catch (_) {
    return PRIMARY_API_URL || '';
  }
})();
const FALLBACK_API_URL = DERIVED_BACKEND_URL;

function isPrivateOrLocalHost(hostname) {
  if (!hostname) return false;
  const h = String(hostname).toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true;
  if (/^10\./.test(h)) return true;
  if (/^192\.168\./.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(h)) return true;
  return false;
}

function getCurrentOriginInfo() {
  const currentOrigin = (window.location.origin || '').replace(/\/+$/, '');
  try {
    const parsed = new URL(currentOrigin);
    const port = parsed.port || (parsed.protocol === 'https:' ? '443' : '80');
    const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    const isDevFrontendPort = port === '3000' || port === '3001';
    return { currentOrigin, parsed, isLocalHost, isDevFrontendPort };
  } catch (_) {
    return { currentOrigin, parsed: null, isLocalHost: false, isDevFrontendPort: false };
  }
}

function shouldForceSameOrigin() {
  const info = getCurrentOriginInfo();
  return Boolean(info.currentOrigin && !info.isLocalHost && !info.isDevFrontendPort);
}

function getApiBaseUrl() {
  // Permite cachear a base funcional
  const cached = sanitizeBaseUrl(localStorage.getItem('apiBaseUrl'));
  const primary = (PRIMARY_API_URL || '').replace(/\/+$/, '');
  const { currentOrigin, isLocalHost, isDevFrontendPort } = getCurrentOriginInfo();
  if (currentOrigin && !isLocalHost && !isDevFrontendPort) {
    try { localStorage.removeItem('apiBaseUrl'); } catch {}
    return currentOrigin;
  }
  if (cached && cached !== currentOrigin) {
    const normalized = String(cached).replace(/\/+$/, '');
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalHost && normalized !== primary) return primary;
    try {
      const parsedCached = new URL(normalized);
      const parsedCurrent = new URL(currentOrigin);
      const cachedHost = parsedCached.hostname;
      const currentHost = parsedCurrent.hostname;
      const isCachedLoopback = cachedHost === 'localhost' || cachedHost === '127.0.0.1' || cachedHost === '::1';
      if (!isLocalHost && isCachedLoopback) {
        try { localStorage.removeItem('apiBaseUrl'); } catch {}
        return primary;
      }
      if (!isPrivateOrLocalHost(currentHost) && isPrivateOrLocalHost(cachedHost) && cachedHost !== currentHost) {
        try { localStorage.removeItem('apiBaseUrl'); } catch {}
        return primary || currentOrigin;
      }
      const cachedPort = parsedCached.port || (parsedCached.protocol === 'https:' ? '443' : '80');
      const currentPort = parsedCurrent.port || (parsedCurrent.protocol === 'https:' ? '443' : '80');
      const isDevFrontendPort = currentPort === '3000' || currentPort === '3001';
      if (!isLocalHost && !isDevFrontendPort && cachedHost === currentHost && cachedPort === '5000' && currentPort !== '5000') {
        try { localStorage.removeItem('apiBaseUrl'); } catch {}
        return currentOrigin;
      }
      if (
        isPrivateOrLocalHost(currentHost) &&
        isPrivateOrLocalHost(cachedHost) &&
        cachedHost !== currentHost
      ) {
        return primary;
      }
    } catch (_) {}
    return normalized;
  }
  try { localStorage.removeItem('apiBaseUrl'); } catch {}
  return primary;
}

function setApiBaseUrl(url) {
  if (shouldForceSameOrigin()) return;
  const normalized = sanitizeBaseUrl(url);
  if (!normalized) return;
  try {
    const currentProtocol = String(window.location.protocol || '').toLowerCase();
    const parsed = new URL(normalized);
    if (currentProtocol === 'https:' && parsed.protocol !== 'https:') return;
  } catch (_) {}
  try { localStorage.setItem('apiBaseUrl', normalized); } catch {}
}

export function resolveApiBaseUrl() {
  return getApiBaseUrl() || '';
}

function shouldInvalidateSession(error) {
  const status = Number(error?.status || 0);
  const code = String(error?.code || '');
  const message = String(error?.message || '').toLowerCase();
  if (status === 401 || status === 403) return true;
  if (status === 400 && (code === 'INVALID_REFRESH_TOKEN' || message.includes('refresh token'))) return true;
  if (code === 'INVALID_REFRESH_TOKEN' || code === 'TOKEN_EXPIRED') return true;
  return false;
}

function extractErrorMessage(payload, status) {
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (trimmed) return trimmed;
  }

  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();
    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error.trim();
    if (payload.error && typeof payload.error === 'object') {
      if (typeof payload.error.message === 'string' && payload.error.message.trim()) return payload.error.message.trim();
      if (typeof payload.error.error === 'string' && payload.error.error.trim()) return payload.error.error.trim();
    }
  }

  return `Erro HTTP ${status}`;
}

// --- Helpers do LocalStorage ---

const getStoredToken = () => {
  try {
    const raw = localStorage.getItem('vortiqaUser');
    if (raw) {
      const parsed = JSON.parse(raw);
      const direct = parsed?.token;
      if (direct) return direct;
      const nested = parsed?.user?.token;
      if (nested) return nested;
    }
  } catch (_) {}
  return null;
};

const saveUserData = (userData) => {
  // Garantir que o usuário tenha companyId
  if (userData.user && !userData.user.companyId) {
    userData.user.companyId = 1; // Valor padrão
  }

  localStorage.setItem('vortiqaUser', JSON.stringify({
    ...userData,
    loginTime: new Date().getTime()
  }));

  // Também salvar na chave 'user' para compatibilidade
  localStorage.setItem('user', JSON.stringify(userData.user));
};

const clearUserData = () => {
  localStorage.removeItem('vortiqaUser');
  localStorage.removeItem('user'); // Limpar a chave de compatibilidade
};

export const getCurrentUser = () => {
  const userData = localStorage.getItem('vortiqaUser');
  return userData ? JSON.parse(userData) : null;
};

// --- Funções de Autenticação ---
// (Usam fetch() direto para evitar dependência circular com 'request')

export const login = async (email, password) => {
  try {
    const forceSameOrigin = shouldForceSameOrigin();
    let base = forceSameOrigin ? (window.location.origin || '').replace(/\/+$/, '') : getApiBaseUrl();
    let response;
    try {
      response = await fetch(`${base}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      if (forceSameOrigin) throw networkErr;
      base = FALLBACK_API_URL;
      setApiBaseUrl(base);
      response = await fetch(`${base}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    }

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      const payload = isJson
        ? await response.json().catch(() => null)
        : await response.text().catch(() => null);
  
      const status = response.status;
      let message;
      if (status === 502) message = 'Servidor indisponível (Bad Gateway - 502). Verifique backend/Nginx.';
      else if (status === 503) message = 'Servidor indisponível (Service Unavailable - 503).';
      else if (status === 504) message = 'Servidor indisponível (Gateway Timeout - 504).';
      else {
        message = extractErrorMessage(payload, status);
      }

      const err = new Error(message);
      err.status = status;
      err.payload = payload;
      throw err;
    }

    const data = isJson
      ? await response.json().catch(() => ({}))
      : await response.text().catch(() => '');

    // Normalizar resposta em caso de texto plano
    const payload = typeof data === 'string' ? {} : data;

    if (payload.token && payload.user) {
      const userData = {
        token: payload.token,
        refreshToken: payload.refreshToken,
        user: payload.user,
        expiresAt: payload.expiresAt
      };

      saveUserData(userData);
      return userData;
    }

    // Se o backend retornar algo diferente, ainda assim devolve o bruto
    return payload;
  } catch (error) {
    throw error;
  }
};

export const refreshToken = async () => {
  try {
    // CORREÇÃO: Usando o helper getCurrentUser()
    const userData = getCurrentUser();
    if (!userData) {
      throw new Error('Nenhum dado de usuário encontrado');
    }

    const { refreshToken: currentRefreshToken } = userData;
    if (!currentRefreshToken) {
      throw new Error('Refresh token não encontrado');
    }

    const forceSameOrigin = shouldForceSameOrigin();
    const base = forceSameOrigin ? (window.location.origin || '').replace(/\/+$/, '') : getApiBaseUrl();
    const ctrl = new AbortController();
    const timeoutMs = 15000;
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    let response;
    try {
      response = await fetch(`${base}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
        credentials: 'include',
        cache: 'no-store',
        signal: ctrl.signal
      });
    } finally {
      clearTimeout(t);
    }

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const err = new Error(extractErrorMessage(payload, response.status) || 'Falha ao renovar token');
      err.status = response.status;
      err.payload = payload;
      err.code = payload?.code || payload?.error?.code || null;
      throw err;
    }

    const data = await response.json();
    
    // CORREÇÃO: Usando o objeto 'userData' já carregado
    const newUserData = {
      ...userData,
      token: data.token,
      refreshToken: data.refreshToken || currentRefreshToken,
      expiresAt: data.expiresAt
    };

    saveUserData(newUserData);
    return data.token;
  } catch (error) {
    if (shouldInvalidateSession(error)) {
      clearUserData();
    }
    throw error;
  }
};

export const logout = async () => {
  try {
    // CORREÇÃO: Usando o wrapper 'request' para invalidar o token no backend
    // O token será adicionado automaticamente por 'request'
    await request('/api/auth/logout', { method: 'POST' });
  } catch (error) {
  } finally {
    // Sempre limpa os dados locais
    clearUserData();
  }
};

// --- Wrapper Principal de Requisição (Autenticado) ---

let refreshInFlight = null;
let logoutInProgress = false;

async function getRefreshedToken() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshToken()
    .then((t) => t)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export async function request(endpoint, options = {}) {
    // CORREÇÃO: API_BASE_URL é lida do escopo do módulo, não redefinida
    const token = getStoredToken();
    const normalizedEndpoint = String(endpoint || '').startsWith('/') ? String(endpoint || '') : `/${String(endpoint || '')}`;

    const body = options.body;
    const method = (options.method || (body ? 'POST' : 'GET')).toUpperCase();

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {})
    };

    const config = {
        method,
        headers,
        credentials: 'include',
        cache: 'no-store',
        ...(options.signal ? { signal: options.signal } : {})
    };

    if (body) {
        config.body = JSON.stringify(body);
    }

    const totalRetries = method === 'GET' ? 3 : 2;

    let lastError;
    for (let attempt = 1; attempt <= totalRetries; attempt++) {
        try {
            const forceSameOrigin = shouldForceSameOrigin();
            let base = forceSameOrigin ? (window.location.origin || '').replace(/\/+$/, '') : getApiBaseUrl();
            const cachedBefore = (() => {
              try { return localStorage.getItem('apiBaseUrl') || ''; } catch { return ''; }
            })();
            let response;
            try {
              response = await fetch(`${base}${normalizedEndpoint}`, config);
            } catch (networkErr) {
              if (forceSameOrigin) throw networkErr;
              const normalizedBase = String(base || '').replace(/\/+$/, '');
              const normalizedCached = String(cachedBefore || '').replace(/\/+$/, '');
              const primary = (PRIMARY_API_URL || '').replace(/\/+$/, '');
              if (normalizedCached && normalizedBase === normalizedCached) {
                try { localStorage.removeItem('apiBaseUrl'); } catch {}
                base = primary || FALLBACK_API_URL;
              } else {
                base = FALLBACK_API_URL;
              }
              setApiBaseUrl(base);
              response = await fetch(`${base}${normalizedEndpoint}`, config);
            }

            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const payload = isJson
                ? await response.json().catch(() => null)
                : await response.text().catch(() => null);

            if (!response.ok) {
                const status = response.status;
                let message;
                if (status === 502) message = 'Servidor indisponível (Bad Gateway - 502). Verifique o backend/Nginx.';
                else if (status === 503) message = 'Servidor indisponível (Service Unavailable - 503).';
                else if (status === 504) message = 'Servidor indisponível (Gateway Timeout - 504).';
                else {
                    message = extractErrorMessage(payload, status);
                }

                const err = new Error(message);
                err.status = status;
                err.payload = payload;
                throw err;
            }

            return payload;
        } catch (err) {
            // @ts-ignore
            // Em 401, tenta renovar token uma vez antes de desistir
            if (err.status === 401 && attempt === 1 && !logoutInProgress) {
                try {
                    const newToken = await getRefreshedToken();
                    config.headers.Authorization = `Bearer ${newToken}`;
                    // Repete imediatamente com token renovado
                    continue;
                } catch (refreshErr) {
                    if (shouldInvalidateSession(refreshErr)) {
                        logoutInProgress = true;
                        clearUserData();
                        window.location.href = '/login'; // Força o logout apenas quando a sessao realmente expirou
                        return Promise.reject(refreshErr);
                    }
                    throw refreshErr;
                }
            }

            if (method !== 'GET' && attempt === 1) {
                throw err;
            }

            lastError = err;
            if (attempt === totalRetries) {
                throw err;
            }
            await new Promise(resolve => setTimeout(resolve, Math.min(1000 * attempt, 3000)));
        }
    }
    // Isso só será alcançado se algo muito estranho acontecer no loop
    throw lastError || new Error("Falha desconhecida na requisição");
}

// Upload com FormData (não definir Content-Type para arquivos)
export async function upload(endpoint, formData, options = {}) {
  const tokenData = localStorage.getItem('vortiqaUser');
  const token = tokenData ? JSON.parse(tokenData)?.token : null;

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {})
  };

  const method = options.method || 'POST';
  const forceSameOrigin = shouldForceSameOrigin();
  let base = forceSameOrigin ? (window.location.origin || '').replace(/\/+$/, '') : getApiBaseUrl();
  const uploadWithXhr = async (resolvedBase) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${resolvedBase}${endpoint}`);
    xhr.withCredentials = true;
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, value);
    });
    if (typeof options.onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        const total = Number(event?.total || 0) || 0;
        const loaded = Number(event?.loaded || 0) || 0;
        options.onProgress({
          loaded,
          total,
          lengthComputable: event?.lengthComputable === true,
          percent: total > 0 ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100))) : null
        });
      };
    }
    xhr.onerror = () => {
      const err = new Error('Falha de rede durante o upload');
      err.isNetworkError = true;
      reject(err);
    };
    xhr.onabort = () => {
      const err = new Error('Upload cancelado');
      err.isNetworkError = true;
      reject(err);
    };
    xhr.onload = () => {
      const contentType = xhr.getResponseHeader('content-type') || '';
      const isJson = contentType.includes('application/json');
      const payload = isJson
        ? (() => { try { return JSON.parse(xhr.responseText || 'null'); } catch { return null; } })()
        : (xhr.responseText || null);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload);
        return;
      }
      const err = new Error(extractErrorMessage(payload, xhr.status));
      err.status = xhr.status;
      err.payload = payload;
      reject(err);
    };
    xhr.send(formData);
  });
  try {
    return await uploadWithXhr(base);
  } catch (networkErr) {
    if (forceSameOrigin) throw networkErr;
    base = FALLBACK_API_URL;
    setApiBaseUrl(base);
    return uploadWithXhr(base);
  }
}

// --- Wrappers estilo Axios (compatibilidade com serviços existentes) ---
// Mantêm a assinatura usada no código legado: api.get/post/put/delete
export async function get(endpoint) {
  return request(endpoint, { method: 'GET' });
}

export async function post(endpoint, data) {
  return request(endpoint, { method: 'POST', body: data });
}

export async function put(endpoint, data) {
  return request(endpoint, { method: 'PUT', body: data });
}

export async function patch(endpoint, data) {
  return request(endpoint, { method: 'PATCH', body: data });
}

export async function requestBlob(endpoint, options = {}) {
  const token = getStoredToken();
  const normalizedEndpoint = String(endpoint || '').startsWith('/') ? String(endpoint || '') : `/${String(endpoint || '')}`;
  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {})
  };
  const config = {
    method,
    headers,
    credentials: 'include',
    cache: 'no-store',
    ...(options.signal ? { signal: options.signal } : {})
  };
  if (options.body) {
    config.body = typeof options.body === 'string' || options.body instanceof FormData || options.body instanceof URLSearchParams
      ? options.body
      : JSON.stringify(options.body);
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
  }
  const forceSameOrigin = shouldForceSameOrigin();
  let base = forceSameOrigin ? (window.location.origin || '').replace(/\/+$/, '') : getApiBaseUrl();
  const primary = (PRIMARY_API_URL || '').replace(/\/+$/, '');
  let attempts = 0;
  const tryOnce = async (b) => {
    attempts += 1;
    try {
      const response = await fetch(`${b}${normalizedEndpoint}`, config);
      if (!response.ok) {
        const ct = response.headers.get('content-type') || '';
        const payload = ct.includes('application/json')
          ? await response.json().catch(() => null)
          : await response.text().catch(() => null);
        const err = new Error(extractErrorMessage(payload, response.status) || `HTTP ${response.status}`);
        err.status = response.status;
        err.payload = payload;
        if (response.status === 401 && attempts === 1) {
          try {
            const newToken = await getRefreshedToken();
            config.headers.Authorization = `Bearer ${newToken}`;
            return tryOnce(b);
          } catch (refreshErr) {
            if (shouldInvalidateSession(refreshErr)) {
              if (!logoutInProgress) {
                logoutInProgress = true;
                clearUserData();
                window.location.href = '/login';
              }
            }
            throw refreshErr;
          }
        }
        throw err;
      }
      return await response.blob();
    } catch (networkErr) {
      if (forceSameOrigin) throw networkErr;
      if (attempts === 1) {
        base = primary || FALLBACK_API_URL;
        setApiBaseUrl(base);
        return tryOnce(base);
      }
      throw networkErr;
    }
  };
  return tryOnce(base);
}

export async function del(endpoint) {
  return request(endpoint, { method: 'DELETE' });
}

// --- Métodos HTTP convenientes (compatibilidade com código legado) ---
// Verificar se o token está próximo do vencimento
export const isTokenExpiringSoon = () => {
  const userData = getCurrentUser();
  if (!userData || !userData.expiresAt) return false;

  const expirationTime = new Date(userData.expiresAt).getTime();
  const currentTime = new Date().getTime();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutos em ms

  return (expirationTime - currentTime) < fiveMinutes;
};

export const getMessages = (conversationId) => {
  return request(`/api/chat/conversations/${conversationId}/messages`);
};

export const sendMessage = (conversationId, message, recipientId) => {
  return request(`/api/whatsapp/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: { message, recipientId }
  });
};

const api = {
  request,
  requestBlob,
  upload,
  get,
  post,
  put,
  patch,
  delete: del,
  login,
  logout,
  refreshToken,
  isTokenExpiringSoon,
  getMessages,
  sendMessage,
};
export default api;
